import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme';
import { LoadingModal } from '../CreateCourse';
import { Toast } from '../Toast';

export type QuestionBankAdminTabKey = 'questions' | 'globalSettings';

export type QuestionBankAdminToast = {
  text: string;
  variant: 'success' | 'error';
};

type QuizConfigFetchResult = {
  minScore: number;
  maxAttempts: number | null;
};

type QuizConfigUpdateBody = {
  minScore: number;
  maxAttempts: number | null;
  timeLimitSeconds: null;
  shuffleQuestions: true;
};

export interface QuestionBankAdminTabsProps {
  /** Conteudo renderizado dentro da aba `Questions List`. */
  questions: ReactNode;
  /** Callback opcional para mostrar toast (ex.: salvar/cancelar questões). */
  onToast?: (toast: QuestionBankAdminToast) => void;
  /** Aba inicial (por padrão: `questions`). */
  initialTab?: QuestionBankAdminTabKey;
  /** GET /v1/Configuration/quiz-config */
  fetchQuizConfig?: () => Promise<QuizConfigFetchResult>;
  /** POST /v1/Configuration/quiz-config */
  updateQuizConfig?: (body: QuizConfigUpdateBody) => Promise<void>;
}

function ButtonWithIcon({
  children,
  ...props
}: {
  children: ReactNode;
  disabled?: boolean;
  onClick: () => void;
  backgroundColor: string;
  color: string;
}) {
  const theme = useTheme();
  const iconSaveSrc =
    '/assets/shared/questionBank/Icon-global-config-save-questionBank.png';
  return (
    <button
      type="button"
      disabled={props.disabled}
      onClick={props.onClick}
      className="e-learning-button"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
        fontSize: theme.typography.fontSize.sm,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeight.medium,
        height: 42,
        backgroundColor: props.backgroundColor,
        color: props.color,
        borderRadius: theme.shape.borderRadius.sm,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        border: 'none',
        outline: 'none',
        opacity: props.disabled ? 0.7 : 1,
        transition: 'opacity 0.2s',
        boxSizing: 'border-box',
      }}
    >
      <img
        src={iconSaveSrc}
        alt=""
        aria-hidden
        style={{
          width: 18,
          height: 18,
          objectFit: 'contain',
          filter: 'none',
          opacity: 1,
          border: 'none',
          outline: 'none',
          boxShadow: 'none',
          display: 'block',
        }}
      />
      {children}
    </button>
  );
}

export function QuestionBankAdminTabs({
  questions,
  onToast,
  initialTab = 'questions',
  fetchQuizConfig,
  updateQuizConfig,
}: QuestionBankAdminTabsProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  /** Ícone raster tintado com o verde principal (`palette.text.primary`). */
  const maskedIconPrimaryGreen = (
    src: string,
    sizePx: number
  ): CSSProperties => ({
    width: sizePx,
    height: sizePx,
    backgroundColor: theme.palette.text.primary,
    WebkitMaskImage: `url(${src})`,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskImage: `url(${src})`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    display: 'block',
  });

  const [activeTab, setActiveTab] =
    useState<QuestionBankAdminTabKey>(initialTab);

  const [toastMessage, setToastMessage] =
    useState<QuestionBankAdminToast | null>(null);
  useEffect(() => {
    if (!toastMessage) return;
    const id = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(id);
  }, [toastMessage]);

  const pushToast = useCallback(
    (toast: QuestionBankAdminToast) => {
      if (onToast) {
        onToast(toast);
      } else {
        setToastMessage(toast);
      }
    },
    [onToast]
  );

  // Defaults visuais: sem valores pré-selecionados até o GET popular.
  // Em caso de falha do GET, tratamos no catch para mostrar `0` e nada selecionado.
  const [cutOffScore, setCutOffScore] = useState<number>(0);
  const [cutOffScoreInput, setCutOffScoreInput] = useState<string>('');
  const [maxAttempts, setMaxAttempts] = useState<number | 'unlimited' | null>(
    null
  );
  const [savingGlobal, setSavingGlobal] = useState(false);

  const contentMaxWidthHalf = useMemo(() => {
    const raw = String(theme.layout.contentMaxWidth ?? '');
    const n = parseFloat(raw);
    if (!Number.isFinite(n) || raw.length === 0) return raw;
    return `${n * 0.4}px`;
  }, [theme.layout.contentMaxWidth]);

  const iconGlobalConfigSrc =
    '/assets/shared/questionBank/Icon-global-config-questionBank.png';
  const iconGlobalAssessmentSrc =
    '/assets/shared/questionBank/Icon-global-config-orange-questionBank.png';
  const iconGlobalSettingInfoSrc =
    '/assets/shared/questionBank/Icon-global-config-info-questionBank.png';
  const iconRetryPolicyInfoSrc =
    '/assets/shared/questionBank/Icon-global-config-info-yellow-questionBank.png';

  useEffect(() => {
    if (!fetchQuizConfig) return;
    let cancelled = false;

    (async () => {
      try {
        const cfg = await fetchQuizConfig();
        if (cancelled) return;

        setCutOffScore(cfg.minScore);
        setCutOffScoreInput(String(cfg.minScore));
        setMaxAttempts(
          cfg.maxAttempts === null ? 'unlimited' : cfg.maxAttempts
        );
      } catch {
        if (cancelled) return;
        setCutOffScore(0);
        setCutOffScoreInput('0');
        setMaxAttempts(null);
        pushToast({
          text: t('questionBank.global_settings.save_error'),
          variant: 'error',
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchQuizConfig, pushToast, t]);

  const handleSaveGlobalSettings = useCallback(async () => {
    if (savingGlobal) return;
    if (!updateQuizConfig) {
      pushToast({
        text: t('questionBank.global_settings.save_error'),
        variant: 'error',
      });
      return;
    }
    if (maxAttempts === null || cutOffScoreInput.trim() === '') return;

    setSavingGlobal(true);
    try {
      const body: QuizConfigUpdateBody = {
        minScore: cutOffScore,
        maxAttempts: maxAttempts === 'unlimited' ? null : maxAttempts,
        timeLimitSeconds: null,
        shuffleQuestions: true,
      };

      await updateQuizConfig(body);
      pushToast({
        text: t('questionBank.global_settings.save_success'),
        variant: 'success',
      });
    } catch {
      pushToast({
        text: t('questionBank.global_settings.save_error'),
        variant: 'error',
      });
    } finally {
      setSavingGlobal(false);
    }
  }, [
    savingGlobal,
    updateQuizConfig,
    maxAttempts,
    cutOffScore,
    cutOffScoreInput,
    pushToast,
    t,
  ]);

  return (
    <>
      {toastMessage && (
        <Toast
          message={toastMessage.text}
          variant={toastMessage.variant}
          role="alert"
        />
      )}

      <style>{`
        /* Escopo: evita duplicar header (título/descrição) somente no painel de Questions List */
        [data-e2e="question-bank-admin-questions-panel"] [data-e2e="question-bank-list"] > h1,
        [data-e2e="question-bank-admin-questions-panel"] [data-e2e="question-bank-list"] > p {
          display: none !important;
        }

        /* Evita “encolher” toolbar/tabela por padding duplicado:
           a página do b2b renderiza title/tabs com padding próprio, então
           removemos o padding interno do QuestionBank apenas no painel de Questions List. */
        [data-e2e="question-bank-admin-questions-panel"] [data-e2e="question-bank-list"] {
          padding: 0 !important;
        }
      `}</style>

      {/* Tabs (Questions List / Global Settings) */}
      <div
        role="tablist"
        aria-label={t('questionBank.tabs.aria_label')}
        style={{
          display: 'flex',
          width: '100%',
          maxWidth: contentMaxWidthHalf,
          margin: 0,
          marginBottom: theme.spacing.md,
          marginRight: 'auto',
          backgroundColor: theme.palette.questionBankGlobalSettings.tabsBg,
          borderRadius: theme.shape.borderRadius.full,
          padding: theme.spacing.xxs,
          gap: theme.spacing.xs,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          boxSizing: 'border-box',
        }}
      >
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'questions'}
          tabIndex={activeTab === 'questions' ? 0 : -1}
          aria-controls="qb-admin-question-bank-panel-questions"
          id="qb-admin-question-bank-tab-questions"
          onClick={() => setActiveTab('questions')}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
            backgroundColor:
              activeTab === 'questions'
                ? theme.palette.background.paper
                : 'transparent',
            border: `${theme.shape.borderWidth.thin} solid transparent`,
            borderRadius: theme.shape.borderRadius.full,
            cursor: 'pointer',
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
            color:
              activeTab === 'questions'
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            minHeight: 36,
          }}
        >
          {t('questionBank.tabs.questions_list')}
        </button>

        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'globalSettings'}
          tabIndex={activeTab === 'globalSettings' ? 0 : -1}
          aria-controls="qb-admin-question-bank-panel-global-settings"
          id="qb-admin-question-bank-tab-global-settings"
          onClick={() => setActiveTab('globalSettings')}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing.sm,
            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
            backgroundColor:
              activeTab === 'globalSettings'
                ? theme.palette.background.paper
                : 'transparent',
            border: `${theme.shape.borderWidth.thin} solid transparent`,
            borderRadius: theme.shape.borderRadius.full,
            cursor: 'pointer',
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
            color:
              activeTab === 'globalSettings'
                ? theme.palette.text.primary
                : theme.palette.text.secondary,
            minHeight: 36,
          }}
        >
          <img
            src={iconGlobalConfigSrc}
            alt=""
            aria-hidden
            style={{
              width: 16,
              height: 16,
              objectFit: 'contain',
              filter: 'none',
              opacity: 1,
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
              display: 'block',
            }}
          />
          {t('questionBank.tabs.global_settings')}
        </button>
      </div>

      <div
        role="tabpanel"
        id="qb-admin-question-bank-panel-questions"
        hidden={activeTab !== 'questions'}
      >
        <div data-e2e="question-bank-admin-questions-panel">{questions}</div>
      </div>

      <div
        role="tabpanel"
        id="qb-admin-question-bank-panel-global-settings"
        hidden={activeTab !== 'globalSettings'}
      >
        <LoadingModal
          open={savingGlobal}
          message={t('questionBank.global_settings.saving')}
        />

        <div
          style={{
            // Mantém alinhamento visual com a tabela do `Questions List`:
            // no painel de questions, há uma regra que zera o padding do container.
            // Sem isso, o Global Settings fica estreito por causa do padding externo.
            padding: 0,
            fontFamily: theme.typography.fontFamily,
          }}
        >
          <div
            style={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.shape.borderRadius.lg,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              padding: theme.spacing.xl,
              // Segue o padrão das outras telas/componentes da QuestionBank:
              // ocupa a largura disponível (depois do padding do container) sem
              // “centralizar” limitando a largura com `contentMaxWidth`.
              width: '100%',
              maxWidth: 'none',
              margin: 0,
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: theme.spacing.md,
              }}
            >
              <div
                style={{
                  width:
                    theme.palette.questionBankGlobalSettings
                      .globalAssessmentHeaderIconBoxSize,
                  height:
                    theme.palette.questionBankGlobalSettings
                      .globalAssessmentHeaderIconBoxSize,
                  borderRadius:
                    theme.palette.questionBankGlobalSettings
                      .globalAssessmentHeaderIconBoxRadius,
                  backgroundColor:
                    theme.palette.questionBankGlobalSettings
                      .globalAssessmentHeaderIconBoxBg,
                  color: theme.palette.roles.text,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <div
                  style={maskedIconPrimaryGreen(iconGlobalAssessmentSrc, 18)}
                  aria-hidden
                />
              </div>

              <div style={{ flex: 1 }}>
                <h2
                  style={{
                    margin: 0,
                    marginBottom: theme.spacing.xs,
                    fontSize: theme.typography.fontSize['2xl'],
                    fontWeight: theme.typography.fontWeight.bold,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t('questionBank.global_settings.title')}
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.fontSize.base,
                    lineHeight: theme.typography.lineHeight.normal,
                  }}
                >
                  {t('questionBank.global_settings.subtitle')}
                </p>
              </div>
            </div>

            {/* Pass/Fail Cut-off Score */}
            <div style={{ marginTop: theme.spacing.xl }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: theme.spacing.lg,
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t('questionBank.global_settings.pass_fail_cutoff.title')}
                  </div>
                  <div
                    style={{
                      marginTop: theme.spacing.xs,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                    }}
                  >
                    {t('questionBank.global_settings.pass_fail_cutoff.help')}
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing.sm,
                  }}
                >
                  <input
                    type="text"
                    inputMode="numeric"
                    value={cutOffScoreInput}
                    onChange={e => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setCutOffScoreInput('');
                        setCutOffScore(0);
                        return;
                      }

                      // Aceita apenas dígitos enquanto digita; evita travar com intermediários.
                      if (!/^\d+$/.test(raw)) return;

                      // Evita valores fora do range exigido (0..100).
                      const n = Number(raw);
                      const clamped = Math.max(0, Math.min(100, n));
                      setCutOffScore(clamped);
                      // Se estourar o range, normaliza visualmente para 100.
                      setCutOffScoreInput(n > 100 ? '100' : raw);
                    }}
                    aria-label={t(
                      'questionBank.global_settings.pass_fail_cutoff.input_aria'
                    )}
                    style={{
                      width: 86,
                      height: 42,
                      padding: theme.spacing.sm,
                      textAlign: 'center',
                      backgroundColor: theme.palette.input.background,
                      border: 'none',
                      borderRadius: theme.shape.borderRadius.sm2,
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  <div
                    style={{
                      fontSize: theme.typography.fontSize['2xl'],
                      fontWeight: theme.typography.fontWeight.bold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    %
                  </div>
                </div>
              </div>

              {/* Blue info banner */}
              <div
                style={{
                  marginTop: theme.spacing.lg,
                  backgroundColor:
                    theme.palette.questionBankGlobalSettings.infoBannerBg,
                  borderRadius: theme.shape.borderRadius.md,
                  padding: theme.spacing.md,
                  display: 'flex',
                  gap: theme.spacing.md,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: theme.shape.borderRadius.full,
                    border: `1px solid ${theme.palette.questionBankGlobalSettings.infoIconBorder}`,
                    color:
                      theme.palette.questionBankGlobalSettings.infoIconBorder,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                  aria-hidden
                >
                  <div
                    style={maskedIconPrimaryGreen(iconGlobalSettingInfoSrc, 12)}
                    aria-hidden
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: theme.typography.fontWeight.semibold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t(
                      'questionBank.global_settings.global_setting_banner.title'
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: theme.spacing.xs,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      lineHeight: theme.typography.lineHeight.normal,
                    }}
                  >
                    {t(
                      'questionBank.global_settings.global_setting_banner.text',
                      { cutOffScore }
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Maximum Attempts per Quiz */}
            <div style={{ marginTop: theme.spacing.xl }}>
              <div
                style={{
                  fontWeight: theme.typography.fontWeight.bold,
                  color: theme.palette.text.primary,
                }}
              >
                {t('questionBank.global_settings.max_attempts.title')}
              </div>
              <div
                style={{
                  marginTop: theme.spacing.xs,
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.fontSize.sm,
                }}
              >
                {t('questionBank.global_settings.max_attempts.help')}
              </div>

              <div
                style={{
                  marginTop: theme.spacing.md,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: theme.spacing.md,
                }}
              >
                {[
                  { value: 1 as const, badge: '1' },
                  { value: 2 as const, badge: '2' },
                  { value: 3 as const, badge: '3' },
                  { value: 'unlimited' as const, badge: '∞' },
                ].map(opt => {
                  const isActive = maxAttempts === opt.value;
                  const subtitle =
                    opt.value === 'unlimited'
                      ? t('questionBank.global_settings.attempts.unlimited')
                      : `${opt.value} ${t(
                          opt.value === 1
                            ? 'questionBank.global_settings.attempts.singular'
                            : 'questionBank.global_settings.attempts.plural'
                        )}`;

                  return (
                    <button
                      key={String(opt.value)}
                      type="button"
                      onClick={() => setMaxAttempts(opt.value)}
                      style={{
                        borderRadius: theme.shape.borderRadius.md,
                        border: isActive
                          ? `2px solid ${theme.palette.primary.main}`
                          : `${theme.shape.borderWidth.thin} solid ${theme.palette.border.main}`,
                        backgroundColor: isActive
                          ? theme.palette.questionBankGlobalSettings
                              .maxAttemptsActiveBg
                          : theme.palette.background.paper,
                        padding: theme.spacing.md,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing.xs,
                        minHeight: 82,
                      }}
                      aria-pressed={isActive}
                    >
                      <div
                        style={{
                          fontSize: theme.typography.fontSize['2xl'],
                          fontWeight: theme.typography.fontWeight.bold,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {opt.badge}
                      </div>
                      <div
                        style={{
                          fontSize: theme.typography.fontSize.sm,
                          fontWeight: theme.typography.fontWeight.medium,
                          color: theme.palette.text.secondary,
                          textAlign: 'center',
                        }}
                      >
                        {subtitle}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Retry Policy banner */}
            <div style={{ marginTop: theme.spacing.lg }}>
              <div
                style={{
                  backgroundColor:
                    theme.palette.questionBankGlobalSettings.retryBannerBg,
                  border: `1px solid ${theme.palette.questionBankGlobalSettings.retryBannerBorder}`,
                  borderRadius: theme.shape.borderRadius.md,
                  padding: theme.spacing.md,
                  display: 'flex',
                  gap: theme.spacing.md,
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: theme.shape.borderRadius.full,
                    border: `1px solid ${theme.palette.questionBankGlobalSettings.retryIconBorder}`,
                    color: theme.palette.primary.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}
                  aria-hidden
                >
                  <div
                    style={maskedIconPrimaryGreen(iconRetryPolicyInfoSrc, 12)}
                    aria-hidden
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontWeight: theme.typography.fontWeight.semibold,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t('questionBank.global_settings.retry_policy.title')}
                  </div>
                  <div
                    style={{
                      marginTop: theme.spacing.xs,
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.fontSize.sm,
                      lineHeight: theme.typography.lineHeight.normal,
                    }}
                  >
                    {maxAttempts === null
                      ? t('questionBank.no_value')
                      : maxAttempts === 'unlimited'
                        ? t(
                            'questionBank.global_settings.retry_policy.unlimited_text'
                          )
                        : maxAttempts === 1
                          ? t(
                              'questionBank.global_settings.retry_policy.singular_text'
                            )
                          : t(
                              'questionBank.global_settings.retry_policy.plural_text',
                              { maxAttempts }
                            )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer button */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: theme.spacing.xl,
              }}
            >
              <ButtonWithIcon
                disabled={
                  savingGlobal ||
                  maxAttempts === null ||
                  cutOffScoreInput.trim() === ''
                }
                onClick={handleSaveGlobalSettings}
                backgroundColor={theme.palette.primary.main}
                color={theme.palette.primary.contrastText}
              >
                {t('questionBank.global_settings.save_button')}
              </ButtonWithIcon>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
